import CloseIcon from '@mui/icons-material/Close'
import { Box, Dialog, DialogContent, DialogTitle, Divider, IconButton, Modal, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { Elements, RecurlyProvider, useRecurly } from '@recurly/react-recurly'
import { useFlag } from '@unleash/proxy-client-react'
import { capitalize } from 'lodash'
import { useEffect, useRef, useState, useCallback } from 'react'
import config from '../../../../config'
import { SubscriptionTokenType } from '../../../../graphql/generated'
import useRum from '../../../../providers/rum/useRum'
import logger from '../../../../utils/logger'
import SubscriptionErrorCodes from '../../errorCodes'
import useSubscriptionUpdate from '../../hooks/useSubscriptionUpate'
import PaymentForm from './PaymentForm'

interface UpdatePaymentDialogProps {
  isOpen: boolean
  onClose: () => void
  planName: string
  billingCycle: string
  total: string
  billingDate?: string
}

interface RecurlyToken {
  type: string
  id: string
}

interface Errors {
  general?: string | null
  card?: string | null
  first_name?: string | null
  last_name?: string | null
  country?: string | null
  postal_code?: string | null
  paypal?: string | null
  apple?: string | null
  cashapp?: string | null
}

const transformErrorMessage = (message: string) => {
  if (message.includes('already has subscription')) {
    return 'You already have a subscription. Please contact support if you need assistance.'
  }
  return message
}

function UpdateRecurlyPaymentDialog({ isOpen, onClose, planName, billingCycle, total, billingDate }: Readonly<UpdatePaymentDialogProps>) {
  const theme = useTheme()
  const formRef = useRef<HTMLFormElement>(null)
  const [open, setOpen] = useState(false)
  const { generateErrorEvent, recordApolloError, recordError } = useRum()
  const recordApolloErrorRef = useRef(recordApolloError)
  const [errors, setErrors] = useState<Errors>({})
  const [token, setToken] = useState<RecurlyToken | null>(null)
  const [tokenType, setTokenType] = useState<SubscriptionTokenType | null>(null)
  const [actionTokenId, setActionTokenId] = useState<string | null>(null)
  const [threeDSToken, setThreeDSToken] = useState<RecurlyToken | null>(null)
  const enableCashApp = useFlag('enableCashApp')
  const recurly = useRecurly()

  const { updatePayment, updatePaymentError, updatePaymentLoading } = useSubscriptionUpdate()
  const updatePaymentRef = useRef(updatePayment)

  const hasPendingOrSuccessfulRequest = !updatePaymentError && updatePaymentLoading

  useEffect(() => {
    if (recurly) {
      recurly.configure(config.recurlyApiKey)
    }
  }, [recurly])

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      if (!formRef.current) return
      event.preventDefault()
      try {
        recurly.token(formRef.current, async (err, tokenResponse) => {
          if (err?.details?.length) {
            logger.error('[error]', err)
            const updatedErrors = {} as Errors
            err.details.forEach((error) => {
              let field = error.field as keyof Errors
              let message = error.messages.join(', ')
              if (['number', 'month', 'year', 'cvv'].includes(field)) {
                field = 'card'
                message = 'Credit card error, expiration month, and expiration year are required. Most cards also require a CVV.'
              } else {
                // We don't need to record field validation errors
                const errorEvent = generateErrorEvent(SubscriptionErrorCodes.RecurlyTokenGenerationFailed, { message, context: { error } })
                recordError(errorEvent)
                message = capitalize(message)
              }
              updatedErrors[field] = message
            })
            setErrors(updatedErrors)
          } else if (tokenResponse) {
            setTokenType(SubscriptionTokenType.CreditCard)
            setToken(tokenResponse)
          }
        })
      } catch (error) {
        logger.error('[recurly error]', error)
        setErrors({ general: 'An error occurred while processing your payment. Please try again.' })
      }
    },
    [generateErrorEvent, recordError, recurly]
  )

  const handleClose = () => {
    setOpen(false)
  }

  useEffect(() => {
    if (open && actionTokenId) {
      const risk = recurly.Risk()
      const threeDSecure = risk.ThreeDSecure({ actionTokenId })

      threeDSecure.on('error', (err) => {
        setErrors({ general: transformErrorMessage('3-D Secure authentication failed. Please try again or use a different payment method.') })
        logger.error('3DS Error:', err)
        setOpen(false)
        setTokenType(null)
        setThreeDSToken(null)
        setToken(null)
      })

      threeDSecure.on('token', (threeDSTokenResult) => {
        setOpen(false)
        setTokenType(SubscriptionTokenType.CreditCard)
        setThreeDSToken(threeDSTokenResult)
      })

      // TODO: This is a temporary fix to attach the 3DSecure dialog to the DOM
      setTimeout(() => {
        const container = document.querySelector('#threeDSecure-container')
        if (container) {
          threeDSecure.attach(container as HTMLElement)
        }
      }, 100) // Slight delay ensures the Dialog has fully rendered
    }
  }, [open, actionTokenId, recurly])

  // handle success
  useEffect(() => {
    if (!token?.id || !tokenType) {
      return
    }

    updatePaymentRef.current({ token: token.id, threeDSToken: threeDSToken?.id ?? null }, (apolloError) => {
      recordApolloErrorRef.current(SubscriptionErrorCodes.RecurlyFinalizeFailed, apolloError)

      const threeDSecureError = apolloError.graphQLErrors.find((error) => error.extensions?.code === 'three_d_secure_action_required')

      // if a 3DS token do not display errors 3DS errors will be caught in a different hook
      if (!threeDSToken?.id) {
        if (threeDSecureError) {
          const tokenId = threeDSecureError.extensions?.actionTokenId as string
          setActionTokenId(tokenId)
          setOpen(true)
          setErrors({})
        } else {
          setTokenType(null)
          setErrors({ general: transformErrorMessage(apolloError.message) })
        }
      }
    })
  }, [token?.id, tokenType, threeDSToken?.id])

  return (
    <>
      <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ textAlign: 'left', padding: '16px 24px', position: 'relative', display: 'flex', alignItems: 'center' }}>
          <Typography color="primary" sx={{ fontWeight: '600', fontSize: { xs: '20px', sm: '24px' } }}>
            Update Payment Method
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              color: theme.palette.grey[500]
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <PaymentForm
              enableCashApp={enableCashApp}
              hasPendingOrSuccessfulRequest={hasPendingOrSuccessfulRequest}
              buttonText="Update Payment Method"
              // eslint-disable-next-line react/jsx-no-bind
              handleSubmit={handleSubmit}
              formRef={formRef}
              errors={errors}
              setErrors={setErrors}
              setToken={setToken}
              setTokenType={setTokenType}
              planName={planName}
              billingCycle={billingCycle}
              total={total}
            />
            {billingDate && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                This payment method will not be used until your next billing cycle on {billingDate}.
              </Typography>
            )}
          </Box>
        </DialogContent>
      </Dialog>
      <Modal
        open={open}
        onClose={handleClose}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Box
          sx={{
            backgroundColor: 'white',
            padding: '20px',
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
            display: 'inline-grid',
            minHeight: '90%',
            width: '500px',
            overflowY: 'auto'
          }}
        >
          <div
            id="threeDSecure-container"
            style={{
              display: 'inline',
              width: '100%',
              height: '100%'
            }}
          />
        </Box>
      </Modal>
    </>
  )
}

export default function UpdatePaymentDialog({ isOpen, onClose, planName, billingCycle, total, billingDate }: Readonly<UpdatePaymentDialogProps>) {
  return (
    <RecurlyProvider publicKey={config.recurlyApiKey}>
      <Elements>
        <UpdateRecurlyPaymentDialog
          isOpen={isOpen}
          onClose={onClose}
          planName={planName}
          billingCycle={billingCycle}
          total={total}
          billingDate={billingDate}
        />
      </Elements>
    </RecurlyProvider>
  )
}
